import { PointCloudSubscene } from "@/components/r3f/effects/point-cloud-subscene";
import { SplatFilteredPass } from "@/components/r3f/effects/splat-filtered-pass";
import { SplatsSubScene } from "@/components/r3f/effects/splats-subscene";
import { CadSubscene } from "@/modes/overview-mode/cad-subscene";
import { CadModelObject, PointCloudObject } from "@/object-cache";
import { selectZoomLensActive } from "@/store/measurement-tool-selector";
import { useAppSelector } from "@/store/store-hooks";
import { useAutoClipBoxToStore } from "@/tools/use-auto-clip-box";
import { SceneFilter } from "@/types/scene-filter";
import {
  ComposeFramebuffersPass,
  EffectPipeline,
  FilteredRenderPass,
  FxaaPass,
  StoreFboPass,
  SubScene,
} from "@faro-lotv/app-component-toolbox";
import { ComposeFramebuffersPass as LotvComposeFramebuffersPass } from "@faro-lotv/lotv";
import { useFrame } from "@react-three/fiber";
import { useMemo, useRef, type JSX } from "react";
import { Object3D } from "three";
import { FlyWalkRenderingAnimation } from "../data-dispatch/flywalk-animation";
import {
  sceneFilter2RenderingState,
  unionRenderingStates,
} from "../data-dispatch/flywalk-rendering-state";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";
import { dispatchRenderObjects, RenderDispatch } from "./render-dispatch";
import { useAnimateOpacity } from "./use-animate-opacity";
import { useZoomLens } from "./use-zoom-lens";

type FlyWalkRenderingPipelineProps = {
  /** The current active point cloud */
  pointCloud: PointCloudObject | null;
  /** The current active CAD model */
  cadModel: CadModelObject | null;
  /** The current scene filter */
  sceneFilter: SceneFilter;
  /** The current flywalk animation */
  animation: FlyWalkRenderingAnimation | null;
  /** Callback to end the current animation */
  endAnimation(): void;
};

/**
 * @returns The rendering pipeline to be used in flywalk mode.
 */
export function FlyWalkRenderingPipeline({
  pointCloud,
  cadModel,
  sceneFilter,
  animation,
  endAnimation,
}: FlyWalkRenderingPipelineProps): JSX.Element {
  const zoomLensOn = useAppSelector(selectZoomLensActive);

  const currentRenderingState = useMemo(() => {
    if (animation) {
      return unionRenderingStates(animation.start, animation.end);
    }
    const state = sceneFilter2RenderingState(sceneFilter);
    if (!zoomLensOn) {
      return state;
    }
    // The composer only applies the zoom lens when it composes exactly two subscenes: the main
    // object outside of the lens and the point cloud inside of it. The floorplan must give way.
    return { ...state, floorPlanPassEnabled: false };
  }, [sceneFilter, animation, zoomLensOn]);

  const { clippingBoxChanging } = useToolsAcquaintance();
  const renderOnDemand = !clippingBoxChanging;

  const composerRef = useRef<LotvComposeFramebuffersPass>(null);

  const animateOpacity = useAnimateOpacity(
    currentRenderingState,
    animation,
    composerRef,
    endAnimation,
    zoomLensOn,
  );

  // Performing dispatch of objects to rendering steps in this useFrame hook
  useFrame(({ scene }, delta) => {
    dispatchRenderObjects(scene, cadModel, pointCloud);
    animateOpacity(delta);
  }, 0);

  const storeFboRef = useAutoClipBoxToStore();

  const {
    floorPlanPassEnabled,
    previousImagePassEnabled,
    panoPassEnabled,
    pointCloudPassEnabled,
    cadModelPassEnabled,
    gaussianSplatsPassEnabled,
  } = currentRenderingState;

  useZoomLens(composerRef, zoomLensOn, pointCloud?.iElement.id);

  return (
    <EffectPipeline>
      {/*
       * Render all opaque objects first,
       * so all (potentially) transparent geometry in the current scene can be composed on top of them later
       */}
      <FilteredRenderPass
        filter={(obj: Object3D) =>
          obj.userData.type === RenderDispatch.OpaquePass
        }
        clear
        clearDepth
      />

      <ComposeFramebuffersPass ref={composerRef} zoomLensEnabled={zoomLensOn}>
        {/* The first subscene contains the floorplan */}
        <SubScene
          filter={(obj: Object3D) =>
            obj.userData.type === RenderDispatch.FloorPlanPass
          }
          enabled={floorPlanPassEnabled}
          transparentBackground
        />
        {/*
         * The second subscene contains the previous image, to implement cross fade
         * (pano/photo/orthomosaic) to (pano/photo/orthomosaic) transition
         */}
        <SubScene
          filter={(obj: Object3D) =>
            obj.userData.type === RenderDispatch.PreviousImagePass
          }
          enabled={previousImagePassEnabled}
          customCamera={animation?.previousCamera}
        />
        {/** The third subscene contains the current image */}
        <SubScene
          filter={(obj: Object3D) =>
            obj.userData.type === RenderDispatch.PanoPass
          }
          enabled={panoPassEnabled}
          customCamera={animation?.currentCamera}
        />
        {/*
         * The fourth subscene contains the Gaussian splats, and is only used while the zoom lens
         * is on. There the splats take the role the pano has for a path image without depth: they
         * are composed outside of the lens, while the point cloud is composed inside of it.
         */}
        <SplatsSubScene
          filter={(obj: Object3D) =>
            obj.userData.type === RenderDispatch.GaussianSplatsPass
          }
          enabled={gaussianSplatsPassEnabled && zoomLensOn}
          renderOnDemand={renderOnDemand}
        />
        {/** The fifth subscene contains the LOD point cloud and is rendered on demand */}
        <PointCloudSubscene
          pointCloud={pointCloud}
          renderOnDemand={renderOnDemand}
          enabled={pointCloudPassEnabled || zoomLensOn}
          filter={(obj: Object3D) =>
            obj.userData.type === RenderDispatch.LodPointCloudPass
          }
        />
        {/** The sixth subscene contains the CAD model */}
        <CadSubscene
          cadModel={cadModel}
          enabled={!!cadModel && cadModelPassEnabled}
          renderOnDemand={renderOnDemand}
          cadFilter={cadFilter}
          needsDispatch={false}
        />
      </ComposeFramebuffersPass>
      {/*
       * Gaussian splats are rendered outside the composer so that it can blend properly
       * with the opaque objects in the scene. Its cached framebuffer bakes in a snapshot of
       * the composed background behind the splats, so while a cross fade animation is changing
       * that background over time it must render every frame instead of reusing a stale snapshot;
       * otherwise the background from the moment the pass was last invalidated (e.g. a
       * still-fading point cloud) would remain baked in until the next invalidation.
       */}
      <SplatFilteredPass
        enabled={gaussianSplatsPassEnabled && !zoomLensOn}
        renderOnDemand={!animation}
      />

      {/* At this point, blit the intermediate rendering result onto an offscreen FBO, to enable depth buffer picking later. */}
      <StoreFboPass ref={storeFboRef} />

      {/* Render all transparent objects in this separate pass, but do not clear the buffers.
        This allows the transparency (from anti-aliasing) to be blended correctly.*/}
      <FilteredRenderPass
        filter={(obj: Object3D) =>
          obj.userData.type === RenderDispatch.CosmeticsPass
        }
        clear={false}
        clearDepth={false}
      />
      {/* The last fxaa pass improves the quality of edges and it also achieves that FilteredRenderPass is not
        the last pass, and therefore will not write itself to screen directly, but it will blend correctly onto the
        read FBO. */}
      <FxaaPass />
    </EffectPipeline>
  );
}

function cadFilter(obj: Object3D): boolean {
  return obj.userData.type === RenderDispatch.CadModelPass;
}
